/* eslint-disable @typescript-eslint/no-explicit-any */
// ============================================================================
// DEPENDENCY: @fingerprintjs/fingerprintjs
//   - v5.x is MIT licensed — safe for production/SaaS use
//   - v4.x was BSL (restricted) — do NOT use v4 in production
//
// IMPORTANT: This module collects identifying information. Ensure your privacy
// policy covers this usage and complies with applicable data protection laws.
// ============================================================================

import { load } from '@fingerprintjs/fingerprintjs';

/**
 * Signals delegated to FingerprintJS.
 *
 * WHY DELEGATE THESE:
 * These are the signals where naive implementations break. FingerprintJS has
 * years of battle-testing across browser versions, OS updates, and GPU driver
 * changes. Rolling our own for these would mean replicating hundreds of edge
 * case fixes. Specific reasons per signal:
 *
 * - canvas: The drawing commands must be carefully chosen to maximize cross-
 *   system divergence. FPJS uses a geometry canvas and a text canvas, handles
 *   Safari's canvas randomization (added in 15.4), normalizes for DPR
 *   differences, and avoids commands that produce identical output everywhere.
 *   A naive "draw a rectangle and toDataURL()" misses most of this.
 *
 * - audio: OfflineAudioContext rendering has timing-dependent quirks in Safari
 *   and Firefox. FPJS handles the baseLatency Infinity edge case (fixed in
 *   v4.6.2), the AudioContext constructor differences across browsers, and
 *   normalizes the oscillator → compressor → analyser pipeline output.
 *
 * - fonts: Side-channel font detection (measuring text width with fallback
 *   fonts) requires careful handling of: browser zoom levels, system font
 *   substitution, macOS font smoothing, and the fact that some browsers
 *   return identical measurements for similar fonts. FPJS tests against a
 *   curated list with known-good baseline measurements.
 *
 * - webgl: The WEBGL_debug_renderer_info extension is being deprecated in
 *   Chrome 110+. FPJS gracefully degrades by using alternative WebGL
 *   parameter combinations when unmasked renderer is unavailable. It also
 *   handles WebGL context loss, context creation failures, and extension
 *   enumeration order normalization.
 *
 * Additionally, FPJS collects some signals we don't need to rewrite:
 * - osCpu, languages, colorDepth, deviceMemory, screenResolution,
 *   hardwareConcurrency, timezone, sessionStorage, localStorage, indexedDB,
 *   colorGamut, invertedColors, forcedColors, monochrome, contrast,
 *   reducedMotion, reducedTransparency, hdr, math, pdfViewerEnabled,
 *   architecture, applePay, vendorFlavors, cookiesEnabled, domBlockers, etc.
 *
 * We extract ALL of these as raw values from result.components so we can
 * inspect them individually. We DON'T use FPJS's visitorId hash — we want
 * raw data for our own correlation engine.
 */
export interface LibraryFingerprint {
  deviceId: string;

  // TODO: type it
  rootComponents: Record<string, unknown>;
}

// ============================================================================
// LIBRARY-BACKED COLLECTOR (FingerprintJS v5)
// ============================================================================

/**
 * WHAT THIS DOES:
 * Loads FingerprintJS, runs its full collection pipeline, then extracts every
 * component's raw value into a flat record. We keep the visitorId as a
 * convenience hash, but the real value is in the decomposed components.
 *
 * WHY WE USE THE LIBRARY HERE:
 * See the detailed justification in the LibraryFingerprint interface above.
 * In short: canvas, audio, fonts, and WebGL collection have dozens of browser-
 * specific edge cases that FPJS maintainers have already fixed. Writing our own
 * would be a maintenance liability for signals that change with every Chrome
 * release.
 *
 * WHAT WE GET FROM FPJS (v5 component list):
 * - canvas (geometry + text data URLs)
 * - audio (oscillator float value)
 * - fonts (detected font list via side-channel measurement)
 * - webgl (vendor, renderer, unmasked strings, extensions, parameters)
 * - osCpu, platform, vendor
 * - languages, timezone
 * - screenResolution, colorDepth
 * - hardwareConcurrency, deviceMemory
 * - sessionStorage, localStorage, indexedDB, openDatabase
 * - colorGamut, invertedColors, forcedColors, monochrome, contrast
 * - reducedMotion, reducedTransparency, hdr
 * - math (tan, atan2, etc.)
 * - pdfViewerEnabled, architecture (32/64bit)
 * - applePay, vendorFlavors (browser vendor detection)
 * - cookiesEnabled, domBlockers, fontPreferences
 *
 * NOTE ON REDUNDANCY:
 * Yes, FPJS collects some of the same signals we collect in-house (timezone,
 * screen, math, storage). This is intentional:
 *   1. Our in-house versions give us the EXACT raw values in a typed struct
 *      (FPJS sometimes normalizes or groups values differently)
 *   2. Comparison between FPJS's value and our own reveals tampering
 *      (if someone patches FPJS's collector but not ours, we catch it)
 *   3. If FPJS is ever blocked by an ad-blocker, our in-house collectors
 *      still produce a usable fingerprint
 */
export async function collectLibrary(): Promise<LibraryFingerprint> {
  try {
    const fp = await load({
      monitoring: false,
    });

    const { components, visitorId } = await fp.get();

    const parsedComponents = Object.fromEntries(
      Object.entries(components).map(([key, component]) => [
        key,
        'error' in component ? { _error: component.error } : component.value,
      ]),
    );

    return {
      deviceId: visitorId,
      rootComponents: parsedComponents,
    };
  } catch (error) {
    // FPJS blocked by ad-blocker, privacy browser, or CSP
    // Our in-house collectors below will still produce a fingerprint
    return {
      deviceId: '',
      rootComponents: {
        _error: `FingerprintJS failed: ${(error as any).message || String(error)}`,
      },
    };
  }
}
